// Symptom catalogue for the Be Bold Health Tracker daily log.
//
// This is the data contract. Every logged symptom references an `id` and a
// `category` from this file, and is stored with a severity of 1 to 5. The app
// records what the user reports. It never interprets what a pattern means.
//
// Labels are bilingual (en / pt). Keep them neutral and descriptive: a symptom
// name, never a diagnosis.

export const SEVERITY_MIN = 1;
export const SEVERITY_MAX = 5;

// Single shared intensity scale across every symptom, so one stored shape works
// for the whole catalogue. Describes how strong something felt, not what it means.
export const SEVERITY_SCALE = [
  { value: 1, label_en: 'Barely there', label_pt: 'Quase nada' },
  { value: 2, label_en: 'Mild', label_pt: 'Leve' },
  { value: 3, label_en: 'Moderate', label_pt: 'Moderado' },
  { value: 4, label_en: 'Strong', label_pt: 'Forte' },
  { value: 5, label_en: 'Severe', label_pt: 'Intenso' },
];

// Ordered for the daily log. `icon` is a Material Symbols name (already loaded).
export const SYMPTOM_CATEGORIES = [
  {
    id: 'cycle',
    label_en: 'Cycle',
    label_pt: 'Ciclo',
    icon: 'water_drop',
    symptoms: [
      { id: 'menstrual_flow', label_en: 'Menstrual flow', label_pt: 'Fluxo menstrual' },
      { id: 'spotting', label_en: 'Spotting', label_pt: 'Escape' },
      { id: 'cramps', label_en: 'Cramps', label_pt: 'Cólicas' },
      { id: 'clots', label_en: 'Clots', label_pt: 'Coágulos' },
    ],
  },
  {
    id: 'pelvic_floor',
    label_en: 'Pelvic floor',
    label_pt: 'Assoalho pélvico',
    icon: 'spa',
    symptoms: [
      { id: 'pelvic_pressure', label_en: 'Heaviness or pressure', label_pt: 'Peso ou pressão' },
      { id: 'pelvic_pain', label_en: 'Pelvic pain', label_pt: 'Dor pélvica' },
      { id: 'pain_with_intimacy', label_en: 'Pain with intimacy', label_pt: 'Dor na intimidade' },
    ],
  },
  {
    id: 'mood',
    label_en: 'Mood',
    label_pt: 'Humor',
    icon: 'mood',
    symptoms: [
      { id: 'low_mood', label_en: 'Low mood', label_pt: 'Humor baixo' },
      { id: 'anxiety', label_en: 'Anxiety', label_pt: 'Ansiedade' },
      { id: 'irritability', label_en: 'Irritability', label_pt: 'Irritabilidade' },
      { id: 'brain_fog', label_en: 'Brain fog', label_pt: 'Névoa mental' },
      { id: 'tearfulness', label_en: 'Tearfulness', label_pt: 'Choro fácil' },
    ],
  },
  {
    id: 'sleep',
    label_en: 'Sleep',
    label_pt: 'Sono',
    icon: 'bedtime',
    symptoms: [
      { id: 'trouble_falling_asleep', label_en: 'Trouble falling asleep', label_pt: 'Dificuldade para dormir' },
      { id: 'night_waking', label_en: 'Waking in the night', label_pt: 'Despertar noturno' },
      { id: 'unrefreshing_sleep', label_en: 'Unrefreshing sleep', label_pt: 'Sono não reparador' },
    ],
  },
  {
    id: 'vasomotor',
    label_en: 'Hot flushes',
    label_pt: 'Ondas de calor',
    icon: 'thermostat',
    symptoms: [
      { id: 'hot_flushes', label_en: 'Hot flushes', label_pt: 'Ondas de calor' },
      { id: 'night_sweats', label_en: 'Night sweats', label_pt: 'Suores noturnos' },
      { id: 'palpitations', label_en: 'Palpitations', label_pt: 'Palpitações' },
    ],
  },
  {
    id: 'pain',
    label_en: 'Pain',
    label_pt: 'Dor',
    icon: 'healing',
    symptoms: [
      { id: 'headache', label_en: 'Headache', label_pt: 'Dor de cabeça' },
      { id: 'joint_pain', label_en: 'Joint pain', label_pt: 'Dor nas articulações' },
      { id: 'back_pain', label_en: 'Back pain', label_pt: 'Dor nas costas' },
      { id: 'breast_tenderness', label_en: 'Breast tenderness', label_pt: 'Sensibilidade nos seios' },
    ],
  },
  {
    id: 'bladder_bowel',
    label_en: 'Bladder and bowel',
    label_pt: 'Bexiga e intestino',
    icon: 'water_full',
    symptoms: [
      { id: 'urinary_urgency', label_en: 'Urinary urgency', label_pt: 'Urgência urinária' },
      { id: 'urinary_leakage', label_en: 'Urinary leakage', label_pt: 'Perda de urina' },
      { id: 'frequent_urination', label_en: 'Frequent urination', label_pt: 'Urinar com frequência' },
      { id: 'bloating', label_en: 'Bloating', label_pt: 'Inchaço' },
      { id: 'constipation', label_en: 'Constipation', label_pt: 'Constipação' },
    ],
  },
];

// Flat lookup: symptom_id to { id, category, label_en, label_pt }.
export const SYMPTOM_INDEX = SYMPTOM_CATEGORIES.reduce((acc, cat) => {
  for (const s of cat.symptoms) {
    acc[s.id] = { ...s, category: cat.id };
  }
  return acc;
}, Object.create(null));

export function categoryById(id) {
  return SYMPTOM_CATEGORIES.find((c) => c.id === id) || null;
}

export function severityLabel(value, lang = 'en') {
  const step = SEVERITY_SCALE.find((s) => s.value === value);
  if (!step) return '';
  return lang === 'pt' ? step.label_pt : step.label_en;
}

// Category display order tuned to the behavioural life stage (menopause_stage:
// pre / peri / meno / post / unsure / null). The full category set is always
// returned: only the order changes, so a clinically relevant symptom (such as
// postmenopausal bleeding under "cycle") is reprioritised, never hidden.
const STAGE_CATEGORY_ORDER = {
  peri: ['mood', 'sleep', 'vasomotor', 'cycle', 'bladder_bowel', 'pelvic_floor', 'pain'],
  meno: ['vasomotor', 'sleep', 'bladder_bowel', 'mood', 'pelvic_floor', 'pain', 'cycle'],
  post: ['vasomotor', 'bladder_bowel', 'pelvic_floor', 'sleep', 'mood', 'pain', 'cycle'],
};

export function orderedCategoriesForStage(stage) {
  const order = STAGE_CATEGORY_ORDER[stage];
  // Reproductive (pre), unsure, and null keep the default order (cycle first).
  if (!order) return SYMPTOM_CATEGORIES;
  const byId = new Map(SYMPTOM_CATEGORIES.map((c) => [c.id, c]));
  const ordered = order.map((id) => byId.get(id)).filter(Boolean);
  // Defensive: append any category missing from the order so the set stays whole.
  for (const c of SYMPTOM_CATEGORIES) {
    if (!order.includes(c.id)) ordered.push(c);
  }
  return ordered;
}
